// Shared "create a presentation on THIS project" mutation.
//
// Both create call sites — the project Actions dropdown (ProjectDetailPage)
// and the Presentation tab's in-context "New presentation" button
// (ProjectPresentationTab) — POST /presentations with { project_id } and land
// the rep in the new builder INSIDE the project sub-nav
// (/projects/:id/presentation/:presentationId). Extracted so the two can't
// drift: the tab's button used to be a dead-end Link to /clients/:id (the
// Accounts page) instead of creating anything, which funnelled the round-40
// stage rail's "New presentation" chip into a cul-de-sac (CEO 2026-07-26).
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { tabsKey } from "@/pages/ProjectDetailPage";

// Friendly, rep-visible copy for a failed create — ported from the
// ClientProjectsTab create pattern: a 4xx surfaces the server's own message
// (err.body.error), everything else falls back to a generic retry line. Never
// leaks a raw status code. Callers render the returned string inline
// (text-rust-600) near the create CTA; null when there's no error.
export function createPresentationErrorMessage(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
    const body = error.body as { error?: string } | null;
    return body?.error ?? "Couldn't create presentation.";
  }
  return "Couldn't create presentation. Try again.";
}

export function useCreatePresentation(projectId: string) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ presentation: { id: string } }>("/presentations", {
        method: "POST",
        body: JSON.stringify({ project_id: projectId }),
      }),
    onSuccess: async (res) => {
      // Invalidate BOTH the tab's presentation list AND the project-detail
      // payload (which feeds the stage rail counts + the left sub-nav) BEFORE
      // navigating. Without this, a rep who creates then returns to the tab
      // inside staleTime sees a stale "No presentations yet" empty state and a
      // rail that under-counts — a lying UI (ux-reviewer 2026-07-26).
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["project", projectId, "presentations"],
        }),
        queryClient.invalidateQueries({
          queryKey: [tabsKey, projectId, "detail"],
        }),
      ]);
      navigate(`/projects/${projectId}/presentation/${res.presentation.id}`);
    },
  });
}
